import { TransitionProps } from '@mui/material/transitions';
import React, {
    forwardRef,
    ReactElement,
    Ref,
    useEffect,
    useRef,
    useState,
} from 'react';
import {
    Button,
    AppBar,
    Toolbar,
    IconButton,
    Typography,
    Slide,
    Dialog,
    CardMedia,
    Box,
    Chip,
    Stack,
} from '@mui/material';
import {
    CloseOutlined,
    RotateLeftOutlined,
    RotateRightOutlined,
} from '@ant-design/icons';
import { FileMetadata } from '../../api/types/cms';
import { useImageBuilder } from '../../utils/image';
import { useTranslation } from '../../contexts/TranslationContext';
import ImageEditor, { ImageEditorRef } from '../image/Editor';

const Transition = forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement;
    },
    ref: Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

type ImagePreviewDialogProps = {
    file?: FileMetadata | null;
    editMode?: boolean;
    onClose?: (closedImage: FileMetadata | null | undefined) => void;
    toggleEditMode?: () => void;
};

export default function ImagePreviewDialog({
    file,
    editMode = false,
    onClose = () => {},
    toggleEditMode = () => {},
}: ImagePreviewDialogProps) {
    const editorRef = useRef<ImageEditorRef>();

    const { buildImageUrl } = useImageBuilder();
    const { i18n, translate } = useTranslation();

    const [open, setOpen] = useState(false);
    const [showEditBar, setShowEditBar] = useState(false);

    const [rotation, setRotation] = useState(0);

    useEffect(() => {
        if (editMode) {
            const timer = setTimeout(() => {
                setShowEditBar(false);
            }, 5000);
            return () => {
                clearTimeout(timer);
            };
        } else {
            setShowEditBar(false);
        }
    }, [showEditBar, editMode]);

    const handleClose = () => {
        onClose(file);
    };

    useEffect(() => {
        if (file) {
            setOpen(true);
        } else {
            setOpen(false);
        }
    }, [file]);

    const onMouseMove = () => {
        if (editMode) {
            setShowEditBar(true);
        }
    };

    return (
        <Dialog
            key={'image-preview-dialog'}
            disableEnforceFocus
            fullScreen
            open={open}
            onClose={handleClose}
            onMouseMove={onMouseMove}
            TransitionComponent={Transition}>
            <AppBar
                sx={{ position: 'relative', zIndex: 2 }}
                enableColorOnDark
                color={editMode ? 'secondary' : 'default'}>
                <Toolbar>
                    {editMode && (
                        <Chip label={'Bearbeitungsmodus'} color="warning" />
                    )}
                    <Typography
                        sx={{ ml: 2, flex: 1 }}
                        variant="h6"
                        component="div">
                        {file?.original_name}
                    </Typography>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close">
                        <CloseOutlined />
                    </IconButton>
                </Toolbar>
            </AppBar>
            <Slide in={showEditBar} direction="down">
                <AppBar
                    sx={{ position: 'fixed', top: 60, zIndex: 1 }}
                    enableColorOnDark
                    color="default">
                    <Toolbar
                        variant="dense"
                        sx={{ justifyContent: 'space-between' }}>
                        <Stack direction="row" alignItems="center" spacing={6}>
                            <Typography>Werkzeuge</Typography>
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}>
                                <IconButton
                                    onClick={() => {
                                        editorRef.current?.rotate(90);
                                    }}>
                                    <RotateRightOutlined />
                                </IconButton>
                                <IconButton
                                    onClick={() => {
                                        editorRef.current?.rotate(-90);
                                    }}>
                                    <RotateLeftOutlined />
                                </IconButton>
                            </Stack>
                        </Stack>
                        <Button
                            variant="contained"
                            size="small"
                            color="success">
                            {translate('form.general.save')}
                        </Button>
                    </Toolbar>
                </AppBar>
            </Slide>
            <Box
                alignItems="center"
                justifyContent="center"
                flexGrow={1}
                display="flex">
                {file && !editMode && (
                    <CardMedia
                        image={buildImageUrl(file)}
                        sx={{
                            width: '100%',
                            height: '100%',
                            backgroundSize: 'contain',
                            zIndex: 0,
                        }}
                    />
                )}
                {file && editMode && (
                    <ImageEditor
                        src={buildImageUrl(file)}
                        editorRef={editorRef}
                    />
                )}
            </Box>
        </Dialog>
    );
}
